import React, { useEffect, useState, useMemo } from "react";
import axios from "axios";

const PAGE_SIZE = 12;

const SmsProdLog = () => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [showFailuresOnly, setShowFailuresOnly] = useState(false);
  const [lastRefreshed, setLastRefreshed] = useState(null);
  const [pharmacies, setPharmacies] = useState([]);
  const [selectedPharmacyId, setSelectedPharmacyId] = useState("");
  const [currentPage, setCurrentPage] = useState(1);

  const fetchLogs = async (pharmacyId) => {
    if (!pharmacyId) return;
    setLoading(true);
    const token = localStorage.getItem("authToken");
    try {
      const response = await axios.get(
        `https://api.pharmacegroup.co.uk/smslogprod?pharmacyId=${pharmacyId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (Array.isArray(response.data)) {
        const sortedLogs = response.data.sort(
          (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
        );
        setLogs(sortedLogs);
        setLastRefreshed(new Date());
      }
    } catch (error) {
      console.error("Error fetching SMS logs:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const stored = JSON.parse(localStorage.getItem("members")) || [];
    setPharmacies(stored);
    if (stored.length > 0) {
      const defaultPharmacyId = stored[0].pharmacyId;
      setSelectedPharmacyId(defaultPharmacyId);
      fetchLogs(defaultPharmacyId);
    }
  }, []);

  useEffect(() => {
    if (selectedPharmacyId) {
      fetchLogs(selectedPharmacyId);
      setCurrentPage(1);
    }
  }, [selectedPharmacyId]);

  useEffect(() => {
    const interval = setInterval(() => {
      if (selectedPharmacyId) fetchLogs(selectedPharmacyId);
    }, 20000);
    return () => clearInterval(interval);
  }, [selectedPharmacyId]);

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    if (isNaN(date)) return "-";
    const dd = String(date.getDate()).padStart(2, "0");
    const mm = String(date.getMonth() + 1).padStart(2, "0");
    const yy = String(date.getFullYear()).slice(2);
    const hh = String(date.getHours()).padStart(2, "0");
    const min = String(date.getMinutes()).padStart(2, "0");
    return `${dd}/${mm}/${yy} ${hh}:${min}`;
  };

  const formatTimeOnly = (date) => {
    if (!date) return "";
    const d = new Date(date);
    return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}:${String(d.getSeconds()).padStart(2, "0")}`;
  };

  const filteredLogs = useMemo(() => {
    const term = searchTerm.toLowerCase();
    return logs.filter((log) => {
      const combined = JSON.stringify(log).toLowerCase();
      const matchesSearch = combined.includes(term);

      let passesFailureCheck = true;
      if (showFailuresOnly) {
        passesFailureCheck = log.sentByBackendAt && !isNaN(new Date(log.sentByBackendAt));
      }
      return matchesSearch && passesFailureCheck;
    });
  }, [logs, searchTerm, showFailuresOnly]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, showFailuresOnly]);

  const totalPages = Math.max(1, Math.ceil(filteredLogs.length / PAGE_SIZE));
  useEffect(() => {
    if (currentPage > totalPages) setCurrentPage(1);
  }, [currentPage, totalPages]);

  const startIndex = (currentPage - 1) * PAGE_SIZE;
  const endIndex = startIndex + PAGE_SIZE;
  const paginatedLogs = filteredLogs.slice(startIndex, endIndex);

  const canPrev = currentPage > 1;
  const canNext = currentPage < totalPages;

  return (
    <div style={{ width: "100vw", height: "100vh", overflow: "auto" }}>
      <div style={{ margin: "10px", display: "flex", alignItems: "center", gap: "10px", flexWrap: "wrap" }}>
        <select
          value={selectedPharmacyId}
          onChange={(e) => setSelectedPharmacyId(e.target.value)}
          style={{ padding: "6px", borderRadius: "4px", fontSize: "14px" }}
        >
          {pharmacies.map((p) => (
            <option key={p.pharmacyId} value={p.pharmacyId}>{p.pharmacyName}</option>
          ))}
        </select>

        <input
          type="text"
          placeholder="Search..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          style={{
            padding: "6px",
            border: "1px solid #ccc",
            borderRadius: "4px",
            width: "300px",
            fontSize: "14px",
          }}
        />
        <label style={{ display: "flex", alignItems: "center", fontSize: "14px" }}>
          <input
            type="checkbox"
            checked={showFailuresOnly}
            onChange={(e) => setShowFailuresOnly(e.target.checked)}
            style={{ marginRight: "5px" }}
          />
          Show failures only
        </label>
        {lastRefreshed && (
          <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: "10px", fontSize: "13px", color: "#555" }}>
            <span>Last refreshed at {formatTimeOnly(lastRefreshed)}</span>
            <button
              onClick={() => fetchLogs(selectedPharmacyId)}
              style={{
                padding: "4px 10px",
                fontSize: "13px",
                borderRadius: "4px",
                border: "1px solid #888",
                cursor: "pointer",
                background: "#f3f4f6",
                color: "#000",
              }}
            >
              Refresh now
            </button>
          </div>
        )}
      </div>

      {loading ? (
        <div>Loading SMS logs...</div>
      ) : filteredLogs.length === 0 ? (
        <div style={{ padding: "10px" }}>No results.</div>
      ) : (
        <>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead style={{ position: "sticky", top: 0, backgroundColor: "#e5e7eb", zIndex: 1 }}>
              <tr>
                <th style={{ padding: "4px", border: "1px solid #d1d5db" }}>Created</th>
                <th style={{ padding: "4px", border: "1px solid #d1d5db" }}>Requested</th>
                <th style={{ padding: "4px", border: "1px solid #d1d5db" }}>Sent on</th>
                <th style={{ padding: "4px", border: "1px solid #d1d5db" }}>Send status</th>
                <th style={{ padding: "4px", border: "1px solid #d1d5db" }}>Delivered</th>
                <th style={{ padding: "4px", border: "1px solid #d1d5db" }}>Backend Sent</th>
                <th style={{ padding: "4px", border: "1px solid #d1d5db" }}>Completed</th>
                <th style={{ padding: "4px", border: "1px solid #d1d5db" }}>Message</th>
                <th style={{ padding: "4px", border: "1px solid #d1d5db" }}>Last Status</th>
              </tr>
            </thead>
            <tbody>
              {paginatedLogs.map((log, index) => {
                let bgColor = "";
                const completed = log.isDeleted === true;
                const backendSentValid = log.sentByBackendAt && !isNaN(new Date(log.sentByBackendAt));

                if (!completed) {
                  bgColor = "#ffbf00"; // amber
                } else if (completed && backendSentValid) {
                  bgColor = "lightcoral";
                } else if (completed && !backendSentValid) {
                  bgColor = "lightgreen";
                } else {
                  bgColor = index % 2 === 0 ? "white" : "#f3f4f6";
                }

                return (
                  <tr key={`${startIndex + index}`} style={{ backgroundColor: bgColor }}>
                    <td style={{ padding: "4px", border: "1px solid #d1d5db", textAlign: "center" }}>{formatDate(log.createdAt)}</td>
                    <td style={{ padding: "4px", border: "1px solid #d1d5db", textAlign: "center" }}>{formatDate(log.androidServiceInvokedAt)}</td>
                    <td style={{ padding: "4px", border: "1px solid #d1d5db", textAlign: "center" }}>{formatDate(log.smsSentReceiptAt)}</td>
                    <td style={{ padding: "4px", border: "1px solid #d1d5db", textAlign: "center" }}>
                      {log.simDeliveryFailReason || "-"}
                    </td>
                    <td style={{ padding: "4px", border: "1px solid #d1d5db", textAlign: "center" }}>{formatDate(log.smsDeliveryReceiptAt)}</td>
                    <td style={{ padding: "4px", border: "1px solid #d1d5db", textAlign: "center" }}>{formatDate(log.sentByBackendAt)}</td>
                    <td style={{ padding: "4px", border: "1px solid #d1d5db", textAlign: "center" }}>
                      <input type="checkbox" checked={log.isDeleted === true} readOnly />
                    </td>
                    <td
                      style={{
                        padding: "4px",
                        border: "1px solid #d1d5db",
                        textAlign: "center",
                        maxWidth: "300px",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        fontSize: "12px",
                      }}
                      title={log.message}
                    >
                      {log.message}
                    </td>
                    <td style={{ padding: "4px", border: "1px solid #d1d5db", textAlign: "center" }}>{log.status}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>

          {/* Pagination Bar */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              padding: "10px",
              borderTop: "1px solid #e5e7eb",
              background: "#fafafa",
              position: "sticky",
              bottom: 0,
            }}
          >
            <span style={{ fontSize: "13px", color: "#555" }}>
              Showing {Math.min(filteredLogs.length, startIndex + 1)}–
              {Math.min(filteredLogs.length, endIndex)} of {filteredLogs.length}
            </span>

            <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: "6px" }}>
          <button
  onClick={() => setCurrentPage(1)}
  disabled={!canPrev}
  style={{
    padding: "6px 10px",
    borderRadius: "6px",
    border: "1px solid #d1d5db",
    background: canPrev ? "#fff" : "#f3f4f6",
    cursor: canPrev ? "pointer" : "not-allowed",
    color: canPrev ? "#000" : "#999", // text always visible
    fontWeight: "bold"
  }}
>
  « First
</button>

<button
  onClick={() => { if (canPrev) setCurrentPage(p => Math.max(1, p - 1)); }}
  disabled={!canPrev}
  style={{
    padding: "6px 10px",
    borderRadius: "6px",
    border: "1px solid #d1d5db",
    background: canPrev ? "#fff" : "#f3f4f6",
    cursor: canPrev ? "pointer" : "not-allowed",
    color: canPrev ? "#000" : "#999",
    fontWeight: "bold"
  }}
>
  ‹ Prev
</button>

<span style={{ fontSize: "13px" }}>
  Page {currentPage} of {totalPages}
</span>

<button
  onClick={() => { if (canNext) setCurrentPage(p => Math.min(totalPages, p + 1)); }}
  disabled={!canNext}
  style={{
    padding: "6px 10px",
    borderRadius: "6px",
    border: "1px solid #d1d5db",
    background: canNext ? "#fff" : "#f3f4f6",
    cursor: canNext ? "pointer" : "not-allowed",
    color: canNext ? "#000" : "#999",
    fontWeight: "bold"
  }}
>
  Next ›
</button>

<button
  onClick={() => setCurrentPage(totalPages)}
  disabled={!canNext}
  style={{
    padding: "6px 10px",
    borderRadius: "6px",
    border: "1px solid #d1d5db",
    background: canNext ? "#fff" : "#f3f4f6",
    cursor: canNext ? "pointer" : "not-allowed",
    color: canNext ? "#000" : "#999",
    fontWeight: "bold"
  }}
>
  Last »
</button>


              <span style={{ fontSize: "13px" }}>
                Page {currentPage} of {totalPages}
              </span>

              <button
                onClick={() => { if (canNext) setCurrentPage(p => Math.min(totalPages, p + 1)); }}
                disabled={!canNext}
                style={{
                  padding: "6px 10px",
                  borderRadius: "6px",
                  border: "1px solid #d1d5db",
                  background: canNext ? "#fff" : "#f3f4f6",
                  cursor: canNext ? "pointer" : "not-allowed",
                }}
              >
                Next ›
              </button>
              <button
                onClick={() => setCurrentPage(totalPages)}
                disabled={!canNext}
                style={{
                  padding: "6px 10px",
                  borderRadius: "6px",
                  border: "1px solid #d1d5db",
                  background: canNext ? "#fff" : "#f3f4f6",
                  cursor: canNext ? "pointer" : "not-allowed",
                }}
              >
                Last »
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default SmsProdLog;
